import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Trash2, Plus, X, ExternalLink, Bell, Rss, TrendingUp, TrendingDown, Zap, Loader2, FileText, Copy } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import SelectMenu from "@/components/SelectMenu";
import OpportunityBadge from "@/components/OpportunityBadge";
import ScoreBadge from "@/components/ScoreBadge";
import AIQuotaBadge from "@/components/AIQuotaBadge";
import { truncWallet, fmtPct, fmtUSD } from "@/lib/format";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
  DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";

export default function Watchlists() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [type, setType] = useState("market");
  const [open, setOpen] = useState(false);

  const { data: lists, isLoading } = useQuery({
    queryKey: ["watchlists"],
    queryFn: async () => (await api.get("/watchlists")).data,
  });

  const createMut = useMutation({
    mutationFn: async () => (await api.post("/watchlists", { name, type })).data,
    onSuccess: () => {
      setName(""); setType("market"); setOpen(false);
      qc.invalidateQueries({ queryKey: ["watchlists"] });
      api.post("/onboarding/progress/created_watchlist").catch(() => {});
      toast.success("Watchlist created");
    },
  });
  const delMut = useMutation({
    mutationFn: async (id) => api.delete(`/watchlists/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["watchlists"] }),
  });

  return (
    <div className="space-y-6" data-testid="watchlists-page">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">Personalization</div>
          <h1 className="font-heading text-2xl font-bold text-zinc-100">Watchlists</h1>
          <p className="text-xs text-zinc-500 mt-1 max-w-lg">
            Curated markets and traders you&apos;re tracking. Every item shows live activity,
            attached signals, and one-click alert creation.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-8 w-full sm:w-auto text-xs bg-zinc-100 text-zinc-950 hover:bg-white"
                    data-testid="btn-new-watchlist">
              <Plus size={12} className="mr-1" /> New watchlist
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800 max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-heading">New watchlist</DialogTitle>
              <DialogDescription className="text-xs text-zinc-500">
                Create a collection of markets or traders to track together.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              <label className="text-xs text-zinc-400">Name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)}
                     data-testid="input-watchlist-name"
                     className="bg-zinc-900 border-zinc-800" />
              <label className="text-xs text-zinc-400 mt-2 block">Type</label>
              <SelectMenu value={type} onChange={setType}
                          options={[{ value: "market", label: "Markets" },
                                    { value: "trader", label: "Traders" }]}
                          testid="select-watchlist-type" />
            </div>
            <DialogFooter>
              <Button onClick={() => createMut.mutate()} disabled={!name || createMut.isPending}
                      data-testid="btn-create-watchlist"
                      className="bg-zinc-100 text-zinc-950 hover:bg-white h-8 text-xs">
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {isLoading && <div className="text-xs text-zinc-500">Loading…</div>}
      <div className="space-y-6">
        {(lists || []).map((wl) => (
          <WatchlistCard key={wl.id} wl={wl}
                          onDelete={() => delMut.mutate(wl.id)} />
        ))}
      </div>
    </div>
  );
}

function WatchlistCard({ wl, onDelete }) {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["watchlist-enriched", wl.id],
    queryFn: async () => (await api.get(`/watchlists/${wl.id}/enriched`)).data,
    refetchInterval: 60_000,
  });

  const digestMut = useMutation({
    mutationFn: async (enabled) =>
      api.post(`/watchlists/${wl.id}/digest`, { enabled }),
    onSuccess: (_r, enabled) => {
      qc.invalidateQueries({ queryKey: ["watchlists"] });
      qc.invalidateQueries({ queryKey: ["watchlist-enriched", wl.id] });
      toast.success(enabled ? "Daily digest enabled" : "Daily digest disabled");
    },
  });

  const removeItem = async (iid) => {
    try {
      await api.delete(`/watchlists/${wl.id}/items/${iid}`);
      qc.invalidateQueries({ queryKey: ["watchlist-enriched", wl.id] });
      qc.invalidateQueries({ queryKey: ["watchlists"] });
      toast.success("Removed from watchlist");
    } catch {
      toast.error("Remove failed");
    }
  };

  const items = data?.items || [];
  const digestOn = !!data?.watchlist?.daily_digest_enabled;

  return (
    <section className="border border-zinc-800 rounded-md" data-testid={`watchlist-${wl.id}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between px-4 py-3 border-b border-zinc-800">
        <div>
          <div className="font-heading font-semibold text-zinc-100">{wl.name}</div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">
            {wl.type} · {items.length} item{items.length === 1 ? "" : "s"}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-[11px] text-zinc-400 cursor-pointer select-none"
                 title="Send a daily summary of this watchlist's activity.">
            <Rss size={11} /> Daily digest
            <Switch checked={digestOn}
                    onCheckedChange={(v) => digestMut.mutate(v)}
                    data-testid={`switch-digest-${wl.id}`} />
          </label>
          <button onClick={onDelete}
                  data-testid={`btn-del-wl-${wl.id}`}
                  className="text-zinc-500 hover:text-rose-400"
                  title="Delete watchlist">
            <Trash2 size={14} />
          </button>
        </div>
      </div>
      {isLoading && <div className="p-4 text-xs text-zinc-500"><Loader2 size={12} className="inline animate-spin mr-1" /> Loading live data…</div>}
      {!isLoading && items.length === 0 && (
        <div className="p-6 text-center text-xs text-zinc-500">
          Empty. Add items from a market or trader detail page — or click Watch on any signal.
        </div>
      )}
      {items.length > 0 && (
        <ul className="divide-y divide-zinc-800/70" data-testid={`watchlist-items-${wl.id}`}>
          {items.map((item) => (
            <li key={item.item_id}
                data-testid={`watchlist-item-${item.item_id}`}
                className="px-4 py-3 hover:bg-zinc-900/40">
              {item.kind === "market" ? (
                <MarketRow wlId={wl.id} item={item} onRemove={() => removeItem(item.item_id)} />
              ) : (
                <TraderRow wlId={wl.id} item={item} onRemove={() => removeItem(item.item_id)} />
              )}
            </li>
          ))}
        </ul>
      )}
      {wl.type === "market" && <WatchlistDigestPreview wl={wl} />}
    </section>
  );
}



function WatchlistDigestPreview({ wl }) {
  const [open, setOpen] = useState(false);
  const { data, isFetching, refetch } = useQuery({
    queryKey: ["watchlist-digest", wl.id],
    queryFn: async () => (await api.get(`/watchlists/${wl.id}/digest`)).data,
    enabled: open,
    staleTime: 30_000,
  });

  const items = data?.items || [];
  const plainText = data ? [
    `${data.watchlist_name} daily digest`,
    `Generated ${new Date(data.generated_at).toLocaleString()}`,
    "",
    items.length ? items.map((item, idx) => {
      const parts = [
        `${idx + 1}. ${item.title || item.item_id}`,
        item.current_probability != null ? `Current: ${(item.current_probability * 100).toFixed(1)}%` : null,
        item.probability_delta_24h != null ? `24h: ${(item.probability_delta_24h >= 0 ? "+" : "")}${(item.probability_delta_24h * 100).toFixed(1)}pp` : null,
        item.active_signals_count ? `${item.active_signals_count} active signal${item.active_signals_count === 1 ? "" : "s"}` : null,
        item.note ? `Note: ${item.note}` : null,
        item.reason ? `Reason: ${item.reason}` : null,
      ].filter(Boolean);
      return parts.join(" · ");
    }).join("\n") : "No digest-ready items right now.",
  ].join("\n") : "";

  const copyDigest = async () => {
    try {
      await navigator.clipboard.writeText(plainText);
      toast.success("Digest copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const copilotMut = useMutation({
    mutationFn: async () => (await api.post(`/intel/watchlists/${wl.id}/copilot`)).data,
    onError: (err) => toast.error(err?.response?.data?.detail?.message || "Watchlist copilot failed"),
  });

  return (
    <div className="px-4 pb-3">
      <button
        type="button"
        onClick={() => {
          setOpen((v) => !v);
          if (!open) setTimeout(() => refetch(), 0);
        }}
        className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-100"
        data-testid={`btn-digest-preview-${wl.id}`}
      >
        <FileText size={11} /> {open ? "Hide digest preview" : "Preview digest"}
      </button>
      {open && (
        <div className="mt-3 rounded-md border border-zinc-800 bg-zinc-950/70 p-3" data-testid={`digest-preview-${wl.id}`}>
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Daily digest preview</div>
              <div className="text-xs text-zinc-400">
                {isFetching ? "Generating…" : `${items.length} digest-ready item${items.length === 1 ? "" : "s"}`}
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <AIQuotaBadge endpoint="watchlist_copilot" compact />
              <Button
                onClick={() => copilotMut.mutate()}
                disabled={copilotMut.isPending}
                className="h-7 text-[11px] bg-zinc-100 text-zinc-950 hover:bg-white"
                data-testid={`btn-watchlist-copilot-${wl.id}`}
              >
                {copilotMut.isPending ? <Loader2 size={10} className="mr-1 animate-spin" /> : <Zap size={10} className="mr-1" />}
                What changed?
              </Button>
              <Button
                onClick={copyDigest}
                disabled={!data || isFetching}
                className="h-7 text-[11px] bg-zinc-800 text-zinc-100 hover:bg-zinc-700"
                data-testid={`btn-copy-digest-${wl.id}`}
              >
                <Copy size={10} className="mr-1" /> Copy
              </Button>
            </div>
          </div>
          <div className="mt-3 space-y-2">
            {isFetching && <div className="text-xs text-zinc-500"><Loader2 size={12} className="inline animate-spin mr-1" /> Loading digest…</div>}
            {copilotMut.data?.text && (
              <div className="rounded-md border border-sky-500/20 bg-sky-500/10 p-3 text-xs leading-relaxed text-sky-100 whitespace-pre-wrap" data-testid={`watchlist-copilot-result-${wl.id}`}>
                {copilotMut.data.text}
              </div>
            )}
            {!isFetching && items.length === 0 && (
              <div className="text-xs text-zinc-500">No digest-ready items right now. Items appear here when they move, collect active signals, or have digest-worthy context.</div>
            )}
            {!isFetching && items.map((item) => (
              <div key={item.item_id} className="rounded border border-zinc-800/80 bg-zinc-900/40 p-3">
                <Link to={`/app/markets/${item.item_id}`} className="text-sm text-zinc-100 hover:underline line-clamp-2">
                  {item.title || item.item_id}
                </Link>
                <div className="mt-1 flex flex-wrap gap-2 text-[11px] text-zinc-500">
                  {item.current_probability != null && <span className="font-mono-num">Current {fmtPct(item.current_probability, 1)}</span>}
                  {item.probability_delta_24h != null && <span className="font-mono-num">24h {(item.probability_delta_24h >= 0 ? "+" : "") + (item.probability_delta_24h * 100).toFixed(1)}pp</span>}
                  {item.active_signals_count ? <span>{item.active_signals_count} active signal{item.active_signals_count === 1 ? "" : "s"}</span> : null}
                  {item.digest_ready && <span className="text-sky-300">Digest-ready</span>}
                </div>
                {(item.note || item.reason) && (
                  <div className="mt-2 space-y-1 text-xs text-zinc-400">
                    {item.note && <div><span className="text-zinc-500">Note:</span> {item.note}</div>}
                    {item.reason && <div><span className="text-zinc-500">Reason:</span> {item.reason}</div>}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function ItemMetaEditor({ wlId, item }) {
  const qc = useQueryClient();
  const meta = item.meta || {};
  const [note, setNote] = useState(meta.note || "");
  const [reason, setReason] = useState(meta.reason || "");
  const [priceMove, setPriceMove] = useState(
    meta.alert_on_price_move_pct != null ? String(meta.alert_on_price_move_pct) : ""
  );
  const [signalAlert, setSignalAlert] = useState(!!meta.alert_on_signal_attached);
  const [consensusAlert, setConsensusAlert] = useState(!!meta.alert_on_consensus_change);
  const [dailyDigest, setDailyDigest] = useState(meta.daily_digest !== false);

  React.useEffect(() => {
    setNote(meta.note || "");
    setReason(meta.reason || "");
    setPriceMove(meta.alert_on_price_move_pct != null ? String(meta.alert_on_price_move_pct) : "");
    setSignalAlert(!!meta.alert_on_signal_attached);
    setConsensusAlert(!!meta.alert_on_consensus_change);
    setDailyDigest(meta.daily_digest !== false);
  }, [meta.note, meta.reason, meta.alert_on_price_move_pct, meta.alert_on_signal_attached, meta.alert_on_consensus_change, meta.daily_digest]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const parsedMove = priceMove === "" ? null : Number(priceMove);
      return (await api.patch(`/watchlists/${wlId}/items/${item.item_id}/meta`, {
        note: note.trim() || null,
        reason: reason.trim() || null,
        alert_on_price_move_pct: Number.isFinite(parsedMove) ? parsedMove : null,
        alert_on_signal_attached: signalAlert,
        alert_on_consensus_change: consensusAlert,
        daily_digest: dailyDigest,
      })).data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["watchlist-enriched", wlId] });
      toast.success("Watchlist item updated");
    },
    onError: () => toast.error("Could not save item settings"),
  });

  return (
    <div className="mt-3 rounded-md border border-zinc-800/80 bg-zinc-950/60 p-3 space-y-3" data-testid={`item-meta-${item.item_id}`}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Note</span>
          <Input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Private note"
            className="h-8 bg-zinc-900 border-zinc-800 text-xs"
            data-testid={`input-note-${item.item_id}`}
          />
        </label>
        <label className="space-y-1">
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">Reason added</span>
          <Input
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Why this is on your radar"
            className="h-8 bg-zinc-900 border-zinc-800 text-xs"
            data-testid={`input-reason-${item.item_id}`}
          />
        </label>
      </div>
      <div className="flex flex-col gap-2 lg:flex-row lg:items-end lg:justify-between">
        <div className="flex flex-wrap items-center gap-4 text-[11px] text-zinc-400">
          <label className="flex items-center gap-2 cursor-pointer select-none">
            Daily digest
            <Switch checked={dailyDigest} onCheckedChange={setDailyDigest} data-testid={`switch-item-digest-${item.item_id}`} />
          </label>
          {item.kind === "market" && (
            <>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                New signal alert
                <Switch checked={signalAlert} onCheckedChange={setSignalAlert} data-testid={`switch-signal-alert-${item.item_id}`} />
              </label>
              <label className="flex items-center gap-2 cursor-pointer select-none">
                Consensus change
                <Switch checked={consensusAlert} onCheckedChange={setConsensusAlert} data-testid={`switch-consensus-alert-${item.item_id}`} />
              </label>
              <label className="flex items-center gap-2">
                Price move %
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={priceMove}
                  onChange={(e) => setPriceMove(e.target.value)}
                  placeholder="0.05"
                  className="h-7 w-20 bg-zinc-900 border-zinc-800 text-xs font-mono-num"
                  data-testid={`input-price-alert-${item.item_id}`}
                />
              </label>
            </>
          )}
        </div>
        <Button
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending}
          className="h-7 text-[11px] bg-zinc-100 text-zinc-950 hover:bg-white self-start lg:self-auto"
          data-testid={`btn-save-meta-${item.item_id}`}
        >
          {saveMut.isPending ? <Loader2 size={10} className="mr-1 animate-spin" /> : null}
          Save settings
        </Button>
      </div>
    </div>
  );
}

function MarketRow({ wlId, item, onRemove }) {
  const convertMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/watchlists/${wlId}/items/${item.item_id}/convert-to-alert`)).data,
    onSuccess: (rule) => toast.success(`Alert created: ${rule.name}`),
    onError: () => toast.error("Failed to create alert"),
  });

  if (item.missing) {
    return (
      <div className="flex items-center justify-between">
        <div className="text-xs text-zinc-500 truncate">
          Missing market · <span className="font-mono-num">{item.item_id.slice(0, 16)}…</span>
        </div>
        <button onClick={onRemove}
                data-testid={`btn-remove-item-${item.item_id}`}
                className="text-zinc-500 hover:text-rose-400"><X size={12} /></button>
      </div>
    );
  }

  const delta = item.probability_delta_24h;
  const deltaUp = delta != null && delta > 0;
  const deltaDown = delta != null && delta < 0;

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0 flex-1">
        <Link to={`/app/markets/${item.item_id}`}
              className="font-heading text-sm text-zinc-100 hover:underline line-clamp-2 flex items-center gap-1">
          {item.title || item.item_id}
          <ExternalLink size={10} className="opacity-50 shrink-0" />
        </Link>
        <div className="text-[11px] text-zinc-500 mt-0.5 flex flex-wrap items-center gap-2">
          <span>{item.category || "—"}</span>
          {item.current_probability != null && (
            <>
              <span>·</span>
              <span className="font-mono-num text-zinc-300">
                {fmtPct(item.current_probability, 1)}
              </span>
            </>
          )}
          {[["1h", item.probability_delta_1h], ["24h", item.probability_delta_24h], ["Since added", item.probability_delta_since_added]].map(([label, value]) => {
            if (value == null) return null;
            const up = value > 0.0001;
            const down = value < -0.0001;
            return (
              <span
                key={label}
                data-testid={`item-delta-${label}-${item.item_id}`}
                className={`inline-flex items-center gap-0.5 font-mono-num text-[10px] px-1.5 py-0.5 rounded ${
                  up ? "text-emerald-300 bg-emerald-500/10" :
                  down ? "text-rose-300 bg-rose-500/10" :
                         "text-zinc-400 bg-zinc-500/10"
                }`}
                title={`${label} probability change.`}
              >
                {up && <TrendingUp size={9} />}
                {down && <TrendingDown size={9} />}
                {label} {(value >= 0 ? "+" : "") + (value * 100).toFixed(1)}pp
              </span>
            );
          })}
          {item.digest_ready && (
            <span className="inline-flex items-center rounded bg-sky-500/10 px-1.5 py-0.5 text-[10px] text-sky-300" title="This item has enough movement or signal activity to include in a digest.">
              Digest-ready
            </span>
          )}
          {item.volume_24h != null && (
            <>
              <span>·</span>
              <span className="font-mono-num">{fmtUSD(item.volume_24h)} 24h</span>
            </>
          )}
        </div>
        {item.top_signal && (
          <Link to={`/app/signals/${item.top_signal.id}`}
                className="mt-1.5 inline-flex items-center gap-2 hover:opacity-80">
            <Zap size={10} className="text-emerald-300" />
            <span className="text-[11px] text-zinc-300">
              {item.active_signals_count} active signal{item.active_signals_count > 1 ? "s" : ""}
            </span>
            <OpportunityBadge rating={item.top_signal.opportunity_rating} />
            <ScoreBadge score={item.top_signal.overall_score} />
          </Link>
        )}
        <ItemMetaEditor wlId={wlId} item={item} />
      </div>
      <div className="shrink-0 flex flex-wrap items-center gap-1">
        <button
          onClick={() => convertMut.mutate()}
          disabled={convertMut.isPending}
          data-testid={`btn-convert-alert-${item.item_id}`}
          title="Create an alert rule that watches this market for new signals."
          className="h-7 px-2 text-[11px] rounded-md border border-zinc-700 bg-zinc-900 text-zinc-200 hover:border-zinc-500 hover:bg-zinc-800 inline-flex items-center gap-1"
        >
          {convertMut.isPending ? <Loader2 size={10} className="animate-spin" /> : <Bell size={10} />}
          Alert
        </button>
        <button onClick={onRemove}
                data-testid={`btn-remove-item-${item.item_id}`}
                title="Remove from watchlist"
                className="text-zinc-500 hover:text-rose-400 p-1"><X size={12} /></button>
      </div>
    </div>
  );
}

function TraderRow({ wlId, item, onRemove }) {
  const convertMut = useMutation({
    mutationFn: async () =>
      (await api.post(`/watchlists/${wlId}/items/${item.item_id}/convert-to-alert`)).data,
    onSuccess: (rule) => toast.success(`Alert created: ${rule.name}`),
    onError: () => toast.error("Failed to create alert"),
  });
  if (item.missing) {
    return (
      <div className="flex items-center justify-between">
        <div className="text-xs text-zinc-500 truncate">
          Missing trader · <span className="font-mono-num">{item.item_id.slice(0, 16)}…</span>
        </div>
        <button onClick={onRemove}
                data-testid={`btn-remove-item-${item.item_id}`}
                className="text-zinc-500 hover:text-rose-400"><X size={12} /></button>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <Link to={`/app/traders/${item.item_id}`}
              className="flex-1 min-w-0 flex items-center gap-3 hover:opacity-90">
        <div className="min-w-0">
          <div className="text-sm text-zinc-100 truncate">
            {item.public_username || truncWallet(item.wallet_address)}
          </div>
          <div className="text-[10px] text-zinc-500 font-mono-num">{truncWallet(item.wallet_address)}</div>
        </div>
      </Link>
      <ScoreBadge score={item.score} />
      <button
        onClick={() => convertMut.mutate()}
        disabled={convertMut.isPending}
        data-testid={`btn-convert-alert-${item.item_id}`}
        title="Create an alert rule that fires when this trader takes a new position."
        className="h-7 px-2 text-[11px] rounded-md border border-zinc-700 bg-zinc-900 text-zinc-200 hover:border-zinc-500 hover:bg-zinc-800 inline-flex items-center gap-1"
      >
        {convertMut.isPending ? <Loader2 size={10} className="animate-spin" /> : <Bell size={10} />}
        Alert
      </button>
      <button onClick={onRemove}
              data-testid={`btn-remove-item-${item.item_id}`}
              className="text-zinc-500 hover:text-rose-400 p-1"><X size={12} /></button>
      </div>
      <ItemMetaEditor wlId={wlId} item={item} />
    </div>
  );
}
